import axios from 'axios';

// Auto-refresh manager for successful POST operations
class AutoRefreshManager {
  constructor() {
    this.queryClient = null;
    this.showNotifications = true; // Can be configured
    this.refreshMappings = {
      // Map API endpoints to query keys that should be refreshed
      '/api/Accounting/customers': ['customers'],
      '/api/Accounting/AddCustomerPayment': ['customerPayments', 'customers'],
      '/api/Accounting/DeleteCustomerPayment': ['customerPayments', 'customers'],
      '/api/Invoices': ['invoices'],
      '/api/Expenses': ['expenses'],
      '/api/JournalEntries': ['journalEntries', 'generalLedger'],
      '/api/Accounting/accounts': ['accounts', 'generalLedger'],
      '/api/Accounting/companies': ['companies'],
      '/api/Accounting/users': ['users'],
      '/api/GeneralLedger': ['generalLedger'],
      '/api/Reports': ['reports', 'savedReports'],
    };
  }

  setQueryClient(client) {
    this.queryClient = client;
  }

  setNotifications(enabled) {
    this.showNotifications = enabled;
  }

  shouldRefresh(response) {
    // Check if it's a successful POST request
    if (response.config.method !== 'post') return false;
    
    // Check if response indicates success
    const data = response.data;
    if (data && typeof data === 'object') {
      // Handle the API format: { Success: true, Message: "...", Data: "...", Errors: [] }
      if (data.Success === true) {
        console.log('✅ POST Success detected:', data.Message || 'Operation completed');
        return true;
      }
      // Handle other success formats
      if (data.success === true) return true;
      if (response.status >= 200 && response.status < 300) return true;
    }
    
    return false;
  }

  getQueryKeysToRefresh(url) {
    // Find matching endpoint patterns
    const keysToRefresh = [];
    
    for (const [endpoint, queryKeys] of Object.entries(this.refreshMappings)) {
      if (url.includes(endpoint)) {
        keysToRefresh.push(...queryKeys);
      }
    }
    
    // Handle specific patterns for nested endpoints
    if (url.includes('/api/Invoices/') && url.includes('/approve')) {
      keysToRefresh.push('invoices');
    }
    if (url.includes('/api/Expenses/') && (url.includes('/approve') || url.includes('/reject'))) {
      keysToRefresh.push('expenses');
    }
    if (url.includes('/api/JournalEntries/') && url.includes('/post')) {
      keysToRefresh.push('journalEntries', 'generalLedger');
    }
    
    return [...new Set(keysToRefresh)]; // Remove duplicates
  }

  showSuccessToast(message) {
    if (!this.showNotifications) return;
    
    // Simple toast notification - can be enhanced with a proper toast library
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      const event = new CustomEvent('show-toast', {
        detail: {
          type: 'success',
          message: message || 'Operation completed successfully',
          duration: 3000
        }
      });
      window.dispatchEvent(event);
    }
  }

  async refreshQueries(response) {
    if (!this.queryClient || !this.shouldRefresh(response)) return;

    const url = response.config.url;
    const queryKeys = this.getQueryKeysToRefresh(url);
    
    if (queryKeys.length > 0) {
      console.log(`� Auto-refreshing queries after successful POST to ${url}:`, queryKeys);
      
      // Show success notification
      if (response.data?.Message) {
        this.showSuccessToast(response.data.Message);
      }
      
      // Invalidate and refetch the relevant queries
      const refreshPromises = queryKeys.map(async (queryKey) => {
        try {
          await this.queryClient.invalidateQueries({ queryKey: [queryKey] });
          console.log(`✅ Refreshed ${queryKey} data`);
        } catch (error) {
          console.warn(`⚠️ Failed to refresh ${queryKey}:`, error);
        }
      });
      
      await Promise.allSettled(refreshPromises);
      
      console.log('📝 Auto-refresh completed for:', queryKeys.join(', '));
    }
  }
}

// Create global instance
export const autoRefreshManager = new AutoRefreshManager();

// Create axios instance with base configuration
const api = axios.create({
  baseURL: 'https://portal.crato.africa',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    
    // Skip auth check for login endpoint
    if (config.url?.includes('/api/Auth/login')) {
      console.log(`Making API request to: ${config.baseURL}${config.url}`);
      return config;
    }
    
    // For all other endpoints, check if token exists
    if (!token) {
      console.warn('No auth token found - redirecting to login');
      // Clear any stale user data
      localStorage.removeItem('current_user');
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      
      // Reject the request
      return Promise.reject(new Error('No authentication token'));
    }
    
    config.headers.Authorization = `Bearer ${token}`;
    console.log(`Making API request to: ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and auto-refresh
api.interceptors.response.use(
  async (response) => {
    console.log(`API Response from ${response.config.url}:`, response.data);
    
    // Trigger auto-refresh for successful POST operations
    try {
      await autoRefreshManager.refreshQueries(response);
    } catch (error) {
      console.warn('Auto-refresh failed:', error);
    }
    
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    
    // Handle authentication errors - logout user if token is invalid
    if (error.response?.status === 401) {
      console.warn('Authentication failed - logging out user');
      // Clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_user');
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      
      return Promise.reject(error);
    }
    
    // Handle specific error cases
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    } else if (error.response) {
      // Server responded with error status
      console.error('Server Error:', error.response.status, error.response.data);
    } else if (error.request) {
      // Request was made but no response received (CORS, network issues)
      console.error('Network Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials) => api.post('/api/Auth/login', credentials),
  register: (userData) => api.post('/api/Auth/register', userData),
};

export const companiesAPI = {
  getAll: () => api.get('/api/Accounting/companies'),
  getById: (id) => api.get(`/api/Accounting/companies/${id}`),
  create: (data) => api.post('/api/Accounting/companies', data),
  update: (id, data) => api.put(`/api/Accounting/companies/${id}`, data),
  delete: (id) => api.delete(`/api/Accounting/companies/${id}`),
};

export const accountsAPI = {
  getAll: (companyId) => api.get(`/api/Accounting/companies/${companyId}/accounts`),
  getById: (id) => api.get(`/api/Accounting/accounts/${id}`),
  create: (data) => api.post('/api/Accounting/accounts', data),
  update: (id, data) => api.put(`/api/Accounting/accounts/${id}`, data),
  delete: (id) => api.delete(`/api/Accounting/accounts/${id}`),
};

export const customersAPI = {
  getAll: (companyId, startDate, endDate) => api.get(`/api/Accounting/companies/${companyId}/customers`, {
    params: { ...(startDate && { startDate }), ...(endDate && { endDate }) }
  }),
  getById: (id) => api.get(`/api/Accounting/customers/${id}`),
  create: (data) => api.post('/api/Accounting/customers', data),
  update: (id, data) => api.put(`/api/Accounting/customers/${id}`, data),
  delete: (id) => api.delete(`/api/Accounting/customers/${id}`),
  getStatement: (companyId, customerId, startDate, endDate) =>
    api.get(`/api/Accounting/companies/${companyId}/customers/${customerId}/statement`, {
      params: { startDate, endDate }
    }),
};

export const customerPaymentsAPI = {
  getAll: (companyId, startDate, endDate) => api.get(`/api/Accounting/companies/${companyId}/customers/payments`, {
    params: { ...(startDate && { startDate }), ...(endDate && { endDate }) }
  }),
  getByCustomer: (companyId, customerId, startDate, endDate) => {
    if (!companyId || !customerId || !startDate || !endDate) {
      return Promise.reject(new Error('Customer payments request requires companyId, customerId, startDate, and endDate.'));
    }

    return api.get(
      `/api/Accounting/companies/${encodeURIComponent(companyId)}/customers/${encodeURIComponent(customerId)}/payments`,
      {
        params: { startDate, endDate }
      }
    );
  },
  create: (data) => api.post('/api/Accounting/AddCustomerPayment', data),
  delete: (paymentId) => api.delete(`/api/Accounting/DeleteCustomerPayment/${paymentId}`),
};

export const invoicesAPI = {
  getAll: (companyId) => api.get(`/api/Invoices/company/${companyId}`),
  getById: (id) => api.get(`/api/Invoices/${id}`),
  create: (data) => api.post('/api/Invoices', data),
  update: (id, data) => api.put(`/api/Invoices/${id}`, data),
  delete: (id) => api.delete(`/api/Invoices/${id}`),
  approve: (id) => api.post(`/api/Invoices/${id}/approve`),
};

export const expensesAPI = {
  getAll: (companyId) => api.get(`/api/Expenses/company/${companyId}`),
  getById: (id) => api.get(`/api/Expenses/${id}`),
  create: (data) => api.post('/api/Expenses', data),
  update: (id, data) => api.put(`/api/Expenses/${id}`, data),
  delete: (id) => api.delete(`/api/Expenses/${id}`),
  approve: (id) => api.post(`/api/Expenses/${id}/approve`),
  reject: (id) => api.post(`/api/Expenses/${id}/reject`),
  getByStatus: (companyId, status) => api.get(`/api/Expenses/company/${companyId}/status/${status}`),
  getByCategory: (companyId, category) => api.get(`/api/Expenses/company/${companyId}/category/${category}`),
  getByDateRange: (companyId, fromDate, toDate) => 
    api.get(`/api/Expenses/company/${companyId}/date-range`, {
      params: { fromDate, toDate }
    }),
};

export const journalEntriesAPI = {
  getAll: (companyId) => api.get(`/api/JournalEntries/company/${companyId}`),
  getById: (id) => api.get(`/api/JournalEntries/${id}`),
  create: (data) => api.post('/api/JournalEntries', data),
  update: (id, data) => api.put(`/api/JournalEntries/${id}`, data),
  delete: (id) => api.delete(`/api/JournalEntries/${id}`),
  post: (id) => api.post(`/api/JournalEntries/${id}/post`),
  getByStatus: (companyId, status) => api.get(`/api/JournalEntries/company/${companyId}/status/${status}`),
  getByDateRange: (companyId, fromDate, toDate) => 
    api.get(`/api/JournalEntries/company/${companyId}/date-range`, {
      params: { fromDate, toDate }
    }),
  validateBalance: (data) => api.post('/api/JournalEntries/validate-balance', data),
};

export const generalLedgerAPI = {
  getAll: (companyId, params = {}) => 
    api.get(`/api/GeneralLedger/company/${companyId}`, { params }),
  getByAccount: (accountId, params = {}) => 
    api.get(`/api/GeneralLedger/account/${accountId}`, { params }),
  create: (data) => api.post('/api/GeneralLedger', data),
  getTrialBalance: (companyId, asOfDate) => 
    api.get(`/api/GeneralLedger/company/${companyId}/trial-balance`, {
      params: asOfDate ? { asOfDate } : {}
    }),
  getAccountBalance: (accountId, asOfDate) => 
    api.get(`/api/GeneralLedger/account/${accountId}/balance`, {
      params: asOfDate ? { asOfDate } : {}
    }),
  getByTransactionType: (companyId, transactionType, params = {}) => 
    api.get(`/api/GeneralLedger/company/${companyId}/transaction-type/${transactionType}`, { params }),
};

export const usersAPI = {
  getAll: (companyId) => api.get('/api/Accounting/users', { 
    params: companyId ? { companyId } : {} 
  }),
  getById: (id) => api.get(`/api/Accounting/users/${id}`),
  create: (data) => api.post('/api/Accounting/users', data),
  update: (id, data) => api.put(`/api/Accounting/users/${id}`, data),
  delete: (id) => api.delete(`/api/Accounting/users/${id}`),
};

export const reportsAPI = {
  generate: (data) => api.post('/api/Reports/generate', data),
  getProfitLoss: (companyId, params = {}) => api.get(`/api/Reports/profit-loss/${companyId}`, { params }),
  getBalanceSheet: (companyId, params = {}) => api.get(`/api/Reports/balance-sheet/${companyId}`, { params }),
  getCashFlow: (companyId, params = {}) => api.get(`/api/Reports/cash-flow/${companyId}`, { params }),
  getTrialBalance: (companyId, params = {}) => api.get(`/api/Reports/trial-balance/${companyId}`, { params }),
  getAccountTransactions: (accountId, params = {}) => api.get(`/api/Reports/account-transactions/${accountId}`, { params }),
  saveCustom: (data) => api.post('/api/Reports/custom', data),
  getSaved: (companyId) => api.get(`/api/Reports/saved/${companyId}`),
  loadSaved: (reportId) => api.get(`/api/Reports/saved/load/${reportId}`),
  deleteSaved: (reportId) => api.delete(`/api/Reports/saved/${reportId}`),
  export: (data, format = 'pdf') => api.post('/api/Reports/export', { ...data, format }, { responseType: 'blob' }),
  getDashboard: (companyId, params = {}) => api.get(`/api/Dashboard`, { params: { ...params, companyId } }),
};

export default api;